import React, { createContext, type FC, useEffect } from "react";
import { commitMutation, graphql, useRefetchableFragment } from "react-relay";

import * as Sentry from "@sentry/react";
import { noop } from "lodash";
import moment from "moment";
import uuidv4 from "uuid/v4";

import { useGroupEmitter } from "~/lib/websocket";
import { environment } from "~/models";
import type { OnSightTourKey } from "~/welcomeChecklist/Tours";

import type { DismissibleElementsMarkElementAsSeenMutation } from "~/__generated__/DismissibleElementsMarkElementAsSeenMutation.graphql";
import type { DismissibleElementsMutation } from "~/__generated__/DismissibleElementsMutation.graphql";
import type {
	DismissibleElementsViewer$data,
	DismissibleElementsViewer$key,
} from "~/__generated__/DismissibleElementsViewer.graphql";

export type DismissibleKey =
	| "GuidanceList__guidanceHelp"
	| "VideoCallButton__googleMeet"
	| "VideoCallButton__zoom__newScope"
	| "CarryForwardBanners__customizeSettings"
	| "NoteHeader__endMeeting"
	| "Meetings__zoomCreateEvent"
	| "HomePanel__overTenHomePanelItems"
	| "HomePanel__removedHomePanelItem"
	| "EventFiltersMenu__tooltip"
	| "AI_settingsPopup"
	| "VoiceDetectionSettingsPopup"
	| "ExtensionSettings__meetingGuidelines"
	| "StreamSwitcher__crossWorkspacePopup"
	| "MeetingRecordingBanner__paywalledBanner"
	| "PostUpgradeModal__seen"
	| "AddManagerModal__dismiss"
	| "AddManagerModal__silence"
	| "AppInterstitial__backoff"
	| "guests__bookmarkBackoff"
	| "AgendaBuilder__Header"
	| "AgendaBuilder__Topic"
	| "UX_EarlyAccessBanner"
	| "EARLY_ACCESS_TOUR"
	| "ShareButton_requestedAccessTooltip";

export type UnseenElementKey = OnSightTourKey;

const dismissElementMutation = graphql`
	mutation DismissibleElementsMutation($input: ViewerDismissedElementInput!) @raw_response_type {
		viewerDismissedElement(input: $input) {
			viewer {
				id
				dismissedElements {
					id
					key
					firstDismissedAt
					lastDismissedAt
					dismissedCount
				}
			}
		}
	}
`;

const markElementAsSeenMutation = graphql`
	mutation DismissibleElementsMarkElementAsSeenMutation($key: String!) @raw_response_type {
		viewerMarkElementAsSeen(key: $key) {
			viewer {
				id
				unseenElements {
					id
					key
					lastAcknowledgedAt
					viewsRemaining
				}
			}
		}
	}
`;

export default function dismissElement(key: DismissibleKey, viewer?: DismissibleElementsViewer$data) {
	let optimisticElements = viewer?.dismissedElements ?? [];
	const elementIndex = optimisticElements.findIndex(e => e.key === key);
	if (elementIndex === -1) {
		optimisticElements = [
			...optimisticElements,
			{
				id: uuidv4(),
				key,
				firstDismissedAt: moment().toISOString(),
				lastDismissedAt: moment().toISOString(),
				dismissedCount: 1,
			},
		];
	} else {
		optimisticElements = [
			...optimisticElements.slice(0, elementIndex),
			{
				...optimisticElements[elementIndex],
				lastDismissedAt: moment().toISOString(),
				dismissedCount: optimisticElements[elementIndex].dismissedCount + 1,
			},
			...optimisticElements.slice(elementIndex + 1),
		];
	}

	commitMutation<DismissibleElementsMutation>(environment, {
		mutation: dismissElementMutation,
		variables: {
			input: { key },
		},
		optimisticResponse: {
			viewerDismissedElement: {
				viewer: {
					id: viewer?.id ?? "viewer",
					dismissedElements: optimisticElements,
				},
			},
		},
	});
}

function markElementAsSeen(key: UnseenElementKey, viewer?: DismissibleElementsViewer$data) {
	let optimisticElements = viewer?.unseenElements ?? [];
	const elementIndex = optimisticElements.findIndex(e => e.key === key);
	if (elementIndex === -1) {
		Sentry.captureMessage(`Element ${key} not found in unseenElements. Something is wrong!`, {
			level: "error",
		});

		optimisticElements = [
			...optimisticElements,
			{ id: uuidv4(), key, lastAcknowledgedAt: moment().toISOString(), viewsRemaining: 0 },
		];
	} else {
		optimisticElements = [
			...optimisticElements.slice(0, elementIndex),
			{
				...optimisticElements[elementIndex],
				lastAcknowledgedAt: moment().toISOString(),
				viewsRemaining: optimisticElements[elementIndex].viewsRemaining - 1,
			},
			...optimisticElements.slice(elementIndex + 1),
		];
	}

	commitMutation<DismissibleElementsMarkElementAsSeenMutation>(environment, {
		mutation: markElementAsSeenMutation,
		variables: { key },
		optimisticResponse: {
			viewerMarkElementAsSeen: {
				viewer: {
					id: viewer?.id ?? "viewer",
					unseenElements: optimisticElements,
				},
			},
		},
	});
}

interface DismissedElement extends Omit<DismissibleElementsViewer$data["dismissedElements"][number], "key"> {
	key: DismissibleKey;
}

interface UnseenElement extends Omit<DismissibleElementsViewer$data["unseenElements"][number], "key"> {
	key: UnseenElementKey;
}

export const DismissibleElementsContext = createContext<{
	dismissedElements: readonly DismissedElement[];
	dismiss: (key: DismissibleKey) => void;
	hasDismissed: (key: DismissibleKey) => boolean;
	unseenElements: readonly UnseenElement[];
	markAsSeen: (key: UnseenElementKey) => void;
	hasAcknowledgementsRemaining: (key: UnseenElementKey) => boolean;
}>({
	dismissedElements: [],
	dismiss: noop,
	hasDismissed: () => false,
	unseenElements: [],
	markAsSeen: noop,
	hasAcknowledgementsRemaining: () => false,
});
DismissibleElementsContext.displayName = "DismissibleElementsContext";

export const DismissibleElementsProvider: FC<{ viewerKey: DismissibleElementsViewer$key }> = ({
	viewerKey,
	children,
}) => {
	const [viewer, refetch] = useRefetchableFragment(
		graphql`
			fragment DismissibleElementsViewer on Viewer @refetchable(queryName: "DismissibleElementsViewerRefetchQuery") {
				id
				user {
					id
				}
				dismissedElements {
					id
					key
					firstDismissedAt
					lastDismissedAt
					dismissedCount
				}
				unseenElements {
					id
					key
					lastAcknowledgedAt
					viewsRemaining
				}
			}
		`,
		viewerKey,
	);

	const emitter = useGroupEmitter(viewer.user && `user.${viewer.user.id.replace(/=/g, "")}`);
	useEffect(() => {
		function onMessage(event: { type: string }) {
			if (event.type === "UNSEEN_ELEMENTS_UPDATED") {
				refetch({}, { fetchPolicy: "store-and-network" });
			}
		}
		if (!emitter) return;
		emitter.on("message", onMessage);
		return () => emitter.off("message", onMessage);
	}, [emitter, refetch]);

	const value = React.useMemo(
		() => ({
			dismissedElements: viewer.dismissedElements as DismissedElement[],
			dismiss: (key: DismissibleKey) => dismissElement(key, viewer),
			hasDismissed: (key: DismissibleKey) => viewer.dismissedElements.some(e => e.key === key),
			unseenElements: viewer.unseenElements as UnseenElement[],
			markAsSeen: (key: UnseenElementKey) => markElementAsSeen(key, viewer),
			hasAcknowledgementsRemaining: (key: UnseenElementKey) => {
				const element = viewer.unseenElements.find(e => e.key === key);
				return element ? element.viewsRemaining > 0 : false;
			},
		}),
		[viewer],
	);

	return <DismissibleElementsContext.Provider value={value}>{children}</DismissibleElementsContext.Provider>;
};
